import { createContext, useContext, useReducer, ReactNode } from 'react'
import {
  DreamityPlanMasterResDto,
  DreamityPlanMasterSaveReqDto,
  DreamityPlanMasterModifyReqDto,
  DreamityItemMasterResDto,
  DreamityItemMasterSaveReqDto,
  DreamityItemMasterModifyReqDto,
} from '../types/master.types'
import { masterApi } from '@/services/api/master.api'

interface MasterState {
  planMasters: DreamityPlanMasterResDto[]
  itemMasters: DreamityItemMasterResDto[]
  selectedPlanMaster: DreamityPlanMasterResDto | null
  selectedItemMaster: DreamityItemMasterResDto | null
  loading: boolean
  error: string | null
}

type MasterAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_PLAN_MASTERS'; payload: DreamityPlanMasterResDto[] }
  | { type: 'SET_ITEM_MASTERS'; payload: DreamityItemMasterResDto[] }
  | { type: 'SET_SELECTED_PLAN_MASTER'; payload: DreamityPlanMasterResDto | null }
  | { type: 'SET_SELECTED_ITEM_MASTER'; payload: DreamityItemMasterResDto | null }
  | { type: 'UPDATE_PLAN_MASTER_IN_LIST'; payload: DreamityPlanMasterResDto }
  | { type: 'UPDATE_ITEM_MASTER_IN_LIST'; payload: DreamityItemMasterResDto }
  | { type: 'DELETE_PLAN_MASTER'; payload: number }
  | { type: 'DELETE_ITEM_MASTER'; payload: number }

interface MasterContextType extends MasterState {
  // 플랜 마스터 전체 조회
  fetchPlanMasters: () => Promise<void>
  // 플랜 마스터 단건 조회
  fetchPlanMasterById: (id: number) => Promise<void>
  // 플랜 마스터 생성
  createPlanMaster: (planMaster: DreamityPlanMasterSaveReqDto) => Promise<void>
  // 플랜 마스터 수정
  updatePlanMaster: (id: number, planMaster: DreamityPlanMasterModifyReqDto) => Promise<void>
  // 플랜 마스터 삭제
  deletePlanMaster: (id: number) => Promise<void>
  // 아이템 마스터 전체 조회
  fetchItemMasters: () => Promise<void>
  // 아이템 마스터 단건 조회
  fetchItemMasterById: (id: number) => Promise<void>
  // 아이템 마스터 생성
  createItemMaster: (itemMaster: DreamityItemMasterSaveReqDto) => Promise<void>
  // 아이템 마스터 수정
  updateItemMaster: (id: number, itemMaster: DreamityItemMasterModifyReqDto) => Promise<void>
  // 아이템 마스터 삭제
  deleteItemMaster: (id: number) => Promise<void>
  // 플랜 마스터 선택
  selectPlanMaster: (planMaster: DreamityPlanMasterResDto | null) => void
  // 아이템 마스터 선택
  selectItemMaster: (itemMaster: DreamityItemMasterResDto | null) => void
}

const MasterContext = createContext<MasterContextType | undefined>(undefined)

const initialState: MasterState = {
  planMasters: [],
  itemMasters: [],
  selectedPlanMaster: null,
  selectedItemMaster: null,
  loading: false,
  error: null,
}

function masterReducer(state: MasterState, action: MasterAction): MasterState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload }
    case 'SET_ERROR':
      return { ...state, error: action.payload }
    case 'SET_PLAN_MASTERS':
      return { ...state, planMasters: action.payload }
    case 'SET_ITEM_MASTERS':
      return { ...state, itemMasters: action.payload }
    case 'SET_SELECTED_PLAN_MASTER':
      return { ...state, selectedPlanMaster: action.payload }
    case 'SET_SELECTED_ITEM_MASTER':
      return { ...state, selectedItemMaster: action.payload }
    case 'UPDATE_PLAN_MASTER_IN_LIST':
      return {
        ...state,
        planMasters: state.planMasters.map((master) =>
          master.id === action.payload.id ? action.payload : master
        ),
        selectedPlanMaster:
          state.selectedPlanMaster?.id === action.payload.id
            ? action.payload
            : state.selectedPlanMaster,
      }
    case 'UPDATE_ITEM_MASTER_IN_LIST':
      return {
        ...state,
        itemMasters: state.itemMasters.map((master) =>
          master.id === action.payload.id ? action.payload : master
        ),
        selectedItemMaster:
          state.selectedItemMaster?.id === action.payload.id
            ? action.payload
            : state.selectedItemMaster,
      }
    case 'DELETE_PLAN_MASTER':
      return {
        ...state,
        planMasters: state.planMasters.filter(
          (master) => master.id !== action.payload
        ),
        selectedPlanMaster:
          state.selectedPlanMaster?.id === action.payload
            ? null
            : state.selectedPlanMaster,
      }
    case 'DELETE_ITEM_MASTER':
      return {
        ...state,
        itemMasters: state.itemMasters.filter(
          (master) => master.id !== action.payload
        ),
        selectedItemMaster:
          state.selectedItemMaster?.id === action.payload
            ? null
            : state.selectedItemMaster,
      }
    default:
      return state
  }
}

interface MasterProviderProps {
  children: ReactNode
}

export function MasterProvider({ children }: MasterProviderProps) {
  const [state, dispatch] = useReducer(masterReducer, initialState)

  /**
   * 플랜 마스터 전체 조회
   */
  const fetchPlanMasters = async () => {
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })
    try {
      const planMasters = await masterApi.fetchPlanMasters()
      dispatch({ type: 'SET_PLAN_MASTERS', payload: planMasters })
      dispatch({ type: 'SET_LOADING', payload: false })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      dispatch({ type: 'SET_LOADING', payload: false })
      return
    }
  }

  /**
   * 플랜 마스터 단건 조회
   */
  const fetchPlanMasterById = async (id: number) => {
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })
    try {
      const planMaster = await masterApi.fetchPlanMasterById(id)
      dispatch({ type: 'SET_SELECTED_PLAN_MASTER', payload: planMaster })
      dispatch({ type: 'SET_LOADING', payload: false })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      dispatch({ type: 'SET_LOADING', payload: false })
      return
    }
  }

  /**
   * 플랜 마스터 생성
   */
  const createPlanMaster = async (planMaster: DreamityPlanMasterSaveReqDto) => {
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })
    try {
      await masterApi.createPlanMaster(planMaster)
      // 생성 후 목록 새로고침
      await fetchPlanMasters()
      dispatch({ type: 'SET_LOADING', payload: false })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      dispatch({ type: 'SET_LOADING', payload: false })
      return
    }
  }

  /**
   * 플랜 마스터 수정
   */
  const updatePlanMaster = async (
    id: number,
    planMaster: DreamityPlanMasterModifyReqDto
  ) => {
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })
    try {
      await masterApi.updatePlanMaster(id, planMaster)
      // 수정 후 상세 정보 다시 조회하여 업데이트
      const updatedPlanMaster = await masterApi.fetchPlanMasterById(id)
      dispatch({ type: 'UPDATE_PLAN_MASTER_IN_LIST', payload: updatedPlanMaster })
      dispatch({ type: 'SET_LOADING', payload: false })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      dispatch({ type: 'SET_LOADING', payload: false })
      return
    }
  }

  /**
   * 플랜 마스터 삭제
   */
  const deletePlanMaster = async (id: number) => {
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })
    try {
      await masterApi.deletePlanMaster(id)
      dispatch({ type: 'DELETE_PLAN_MASTER', payload: id })
      dispatch({ type: 'SET_LOADING', payload: false })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      dispatch({ type: 'SET_LOADING', payload: false })
      return
    }
  }

  /**
   * 아이템 마스터 전체 조회
   */
  const fetchItemMasters = async () => {
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })
    try {
      const itemMasters = await masterApi.fetchItemMasters()
      dispatch({ type: 'SET_ITEM_MASTERS', payload: itemMasters })
      dispatch({ type: 'SET_LOADING', payload: false })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      dispatch({ type: 'SET_LOADING', payload: false })
      return
    }
  }

  /**
   * 아이템 마스터 단건 조회
   */
  const fetchItemMasterById = async (id: number) => {
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })
    try {
      const itemMaster = await masterApi.fetchItemMasterById(id)
      dispatch({ type: 'SET_SELECTED_ITEM_MASTER', payload: itemMaster })
      dispatch({ type: 'SET_LOADING', payload: false })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      dispatch({ type: 'SET_LOADING', payload: false })
      throw error
    }
  }

  /**
   * 아이템 마스터 생성
   */
  const createItemMaster = async (itemMaster: DreamityItemMasterSaveReqDto) => {
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })
    try {
      await masterApi.createItemMaster(itemMaster)
      // 생성 후 목록 새로고침
      await fetchItemMasters()
      dispatch({ type: 'SET_LOADING', payload: false })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      dispatch({ type: 'SET_LOADING', payload: false })
      throw error
    }
  }

  /**
   * 아이템 마스터 수정
   */
  const updateItemMaster = async (
    id: number,
    itemMaster: DreamityItemMasterModifyReqDto
  ) => {
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })
    try {
      await masterApi.updateItemMaster(id, itemMaster)
      // 수정 후 상세 정보 다시 조회하여 업데이트
      const updatedItemMaster = await masterApi.fetchItemMasterById(id)
      dispatch({ type: 'UPDATE_ITEM_MASTER_IN_LIST', payload: updatedItemMaster })
      dispatch({ type: 'SET_LOADING', payload: false })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      dispatch({ type: 'SET_LOADING', payload: false })
      throw error
    }
  }

  /**
   * 아이템 마스터 삭제
   */
  const deleteItemMaster = async (id: number) => {
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })
    try {
      await masterApi.deleteItemMaster(id)
      dispatch({ type: 'DELETE_ITEM_MASTER', payload: id })
      dispatch({ type: 'SET_LOADING', payload: false })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      dispatch({ type: 'SET_LOADING', payload: false })
      throw error
    }
  }

  /**
   * 플랜 마스터 선택
   */
  const selectPlanMaster = (planMaster: DreamityPlanMasterResDto | null) => {
    dispatch({ type: 'SET_SELECTED_PLAN_MASTER', payload: planMaster })
  }

  /**
   * 아이템 마스터 선택
   */
  const selectItemMaster = (itemMaster: DreamityItemMasterResDto | null) => {
    dispatch({ type: 'SET_SELECTED_ITEM_MASTER', payload: itemMaster })
  }

  return (
    <MasterContext.Provider
      value={{
        ...state,
        fetchPlanMasters,
        fetchPlanMasterById,
        createPlanMaster,
        updatePlanMaster,
        deletePlanMaster,
        fetchItemMasters,
        fetchItemMasterById,
        createItemMaster,
        updateItemMaster,
        deleteItemMaster,
        selectPlanMaster,
        selectItemMaster,
      }}
    >
      {children}
    </MasterContext.Provider>
  )
}

// useMaster hook은 hooks/useMaster.ts에서 export합니다
export function useMasterContext() {
  const context = useContext(MasterContext)
  if (context === undefined) {
    throw new Error('useMaster must be used within a MasterProvider')
  }
  return context
}

